import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { connect } from 'react-redux';
import classNames from 'classnames';
import * as actionCreators from '../../actions';
import { sendAccessToken } from '../../queries/mutations';
import { getLangDefinition } from '../../utils/helpers';
import HeaderPlaceholder from '../Placeholders/HeaderPlaceholder';
import AdblockerDetector from '../Banner/BlockerDetector';
import FooterPlaceholder from '../Placeholders/FooterPlaceholder';
import Loader from '../Loader';
import AuthModal from '../AuthModal';
import DetailsPage from '../Views/DetailsPage';

const WhiteHeader = dynamic(() => import('../WhiteHeader'), {
  loading: HeaderPlaceholder,
  ssr: false
});

const TabBarNavigation = dynamic(() => import('../TabBarNavigation'), {
  loading: HeaderPlaceholder,
  ssr: false
});

const Footer = dynamic(() => import('../Footer'), {
  loading: FooterPlaceholder,
  ssr: false
});

const mapStateToProps = state => {
  return {
    isVisible: state.drawerUI.isVisible,
    isDetailsVisible: state.detailsModal?.isVisible,
    isAuthVisible: state?.authModal?.isVisible
  };
};

const Layout = ({
  children,
  setBlockerDetector,
  toggleDetailsModal,
  isAuthVisible,
  isDetailsVisible,
  toggleAuthModal,
  headerTitle,
  isBottomNavigation = true,
  isFixedHeader = false
}) => {
  const {
    i18n: { language }
  } = useTranslation();
  const router = useRouter();
  const allClasses = classNames(
    'main-content',
    `locale-${getLangDefinition(language)}`
  );

  const closeAuthModalHandler = () => {
    toggleAuthModal(false);

    router.push(`${router.asPath.split('#')[0]}`, undefined, {
      shallow: true
    });
  };
  const accessToken = router?.query.access_token;
  const [mutateToken, { loading }] = useMutation(sendAccessToken);

  useMemo(() => {
    if (accessToken) {
      mutateToken({
        variables: { accessToken }
      });
    }
  }, []);

  useEffect(() => {
    if (router?.asPath.match('#authentication')) {
      toggleAuthModal(true);
    } else if (router?.asPath.match('#more')) {
      toggleDetailsModal(true);
    }

    router?.events.on('hashChangeComplete', url => {
      if (url.match('#authentication')) {
        toggleAuthModal(true);
      } else {
        toggleAuthModal(false);
      }

      if (url.match('#more')) {
        toggleDetailsModal(true);
      } else {
        toggleDetailsModal(false);
      }
    });
  }, []);

  if (loading) return null;

  return (
    <div className={allClasses}>
      <WhiteHeader {...{ headerTitle, isFixedHeader }} />
      <AdblockerDetector setBlockerDetector={setBlockerDetector} />
      {children}
      <Loader pageLoader={true} />
      {isBottomNavigation && <TabBarNavigation />}
      {!isDetailsVisible && <Footer />}
      {isDetailsVisible && <DetailsPage />}
      {isAuthVisible && (
        <AuthModal
          isOpen={isAuthVisible}
          modalId="auth"
          closeHandler={closeAuthModalHandler}
        />
      )}
    </div>
  );
};

Layout.propTypes = {
  children: PropTypes.node,
  toggleDrawer: PropTypes.func,
  toggleAuthModal: PropTypes.func,
  isVisible: PropTypes.bool,
  isAuthVisible: PropTypes.bool,
  isDetailsVisible: PropTypes.bool,
  setBlockerDetector: PropTypes.func,
  toggleDetailsModal: PropTypes.func,
  isBottomNavigation: PropTypes.bool,
  isFixedHeader: PropTypes.bool,
  headerTitle: PropTypes.string
};

export default connect(mapStateToProps, actionCreators)(Layout);
